/**
 * AppointmentFormSlideOver — right-side panel hosting either the
 * "Schedule Appointment" form (create mode) or the same form
 * pre-filled (edit mode). 1:1 with the folder's
 *   /Users/gudupuramesh/Documents/design ui/anti Interior io ui web/app.js
 *   → `openDetails('create-appointment')` template.
 *
 * Field set (verbatim from folder + lead picker):
 *   - Meeting Title*
 *   - Client / Lead* (select from `useLeads()` + Other / no lead)
 *   - Meeting Type (purpose select)
 *   - Date*
 *   - Start Time* + End Time
 *   - Location Mode (drives auto-fill of next field)
 *   - Address / Link
 *   - Agenda & Notes
 *
 * Internally stores `durationMins = (endTime - startTime) / 60s` so
 * the existing `scheduledAt + durationMins` shape on the Firestore
 * doc stays compatible.
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useAuth } from '@/auth/AuthProvider';
import {
  createAppointment,
  updateAppointment,
} from '@/lib/appointments';
import { useLeads } from '@/lib/useLeads';
import {
  LOCATION_MODE_OPTIONS,
  MEETING_PURPOSE_OPTIONS,
  useAppointment,
  type AppointmentType,
  type LocationMode,
  type MeetingPurpose,
} from '@/lib/useAppointments';

export type AppointmentFormMode =
  | { kind: 'create'; defaultDate?: string }
  | { kind: 'edit'; apptId: string };

export type AppointmentFormSlideOverProps = {
  open: boolean;
  onClose: () => void;
  orgId: string | null;
  mode: AppointmentFormMode | null;
};

// ───── Inline styles — copy of the design folder's form inputs ─────

const STYLE_INPUT: React.CSSProperties = {
  width: '100%',
  padding: 10,
  borderRadius: 6,
  border: '1px solid var(--border)',
  background: '#fff',
  fontSize: 13,
  color: 'var(--text-primary)',
  fontFamily: 'inherit',
};

const STYLE_LABEL: React.CSSProperties = {
  fontSize: 12,
  fontWeight: 600,
  marginBottom: 6,
  display: 'block',
  color: 'var(--text-secondary)',
};

const STYLE_REQUIRED: React.CSSProperties = { color: 'var(--critical)' };

const STYLE_SECTION: React.CSSProperties = {
  borderTop: '1px solid var(--border)',
  paddingTop: 20,
};

const STYLE_GROUP: React.CSSProperties = { marginTop: 12 };

// ───── Helpers ─────

function todayIso(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function combineDateTime(date: string, hhmm: string): Date {
  return new Date(`${date}T${hhmm || '10:00'}:00`);
}

function minutesBetween(startHhmm: string, endHhmm: string): number {
  const [sh, sm] = startHhmm.split(':').map(Number);
  const [eh, em] = endHhmm.split(':').map(Number);
  const start = (sh || 0) * 60 + (sm || 0);
  const end = (eh || 0) * 60 + (em || 0);
  const diff = end - start;
  return diff > 0 ? diff : 60; // fall back to 1h
}

function extractHHMM(d: Date): string {
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

function extractISODate(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function addMinutes(hhmm: string, mins: number): string {
  const [h, m] = hhmm.split(':').map(Number);
  const total = (h || 0) * 60 + (m || 0) + mins;
  const out = ((total % 1440) + 1440) % 1440;
  return `${String(Math.floor(out / 60)).padStart(2, '0')}:${String(out % 60).padStart(2, '0')}`;
}

/** Maps the new MeetingPurpose → legacy AppointmentType so existing
 *  consumers (list-view colour, month-view pill colour) keep working
 *  until everything reads `meetingPurpose` directly. */
function legacyTypeFor(purpose: MeetingPurpose, locationMode: LocationMode): AppointmentType {
  if (locationMode === 'online') return 'virtual_call';
  if (locationMode === 'office') return 'office_meeting';
  if (purpose === 'site_measurement' || purpose === 'site_visit') return 'site_visit';
  if (locationMode === 'site')   return 'site_visit';
  return 'other';
}

// ───── Component ─────

export function AppointmentFormSlideOver({
  open,
  onClose,
  orgId,
  mode,
}: AppointmentFormSlideOverProps) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isEdit = mode?.kind === 'edit';
  const apptId = isEdit ? mode.apptId : undefined;
  const { appointment } = useAppointment(isEdit ? apptId : undefined);

  // Leads for the picker — already paginated under the hood; covers
  // the 200 most-recent leads which is enough for picking.
  const { leads } = useLeads();

  // Form state
  const [title, setTitle] = useState('');
  const [leadId, setLeadId] = useState<string>(''); // '' = no lead
  const [purpose, setPurpose] = useState<MeetingPurpose>('initial_consultation');
  const [date, setDate] = useState(todayIso());
  const [startTime, setStartTime] = useState('10:00');
  const [endTime, setEndTime] = useState('11:00');
  const [locationMode, setLocationMode] = useState<LocationMode>('site');
  const [locationText, setLocationText] = useState('');
  const [clientName, setClientName] = useState('');
  const [clientPhone, setClientPhone] = useState('');
  const [assignedToName, setAssignedToName] = useState('');
  const [notes, setNotes] = useState('');

  // UI
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Seed for create (open / default date change).
  useEffect(() => {
    if (!open) return;
    if (mode?.kind !== 'create') return;
    setTitle('');
    setLeadId('');
    setPurpose('initial_consultation');
    setDate(mode.defaultDate ?? todayIso());
    setStartTime('10:00');
    setEndTime('11:00');
    setLocationMode('site');
    setLocationText('');
    setClientName('');
    setClientPhone('');
    setAssignedToName('');
    setNotes('');
    setError(null);
  }, [open, mode]);

  // Seed for edit when the lead doc loads.
  useEffect(() => {
    if (!open) return;
    if (mode?.kind !== 'edit') return;
    if (!appointment) return;
    const sched = appointment.scheduledAt
      ? new Date(appointment.scheduledAt.toMillis())
      : new Date();
    const mins =
      appointment.durationMins && appointment.durationMins > 0
        ? appointment.durationMins
        : 60;
    setTitle(appointment.title ?? '');
    setLeadId(appointment.leadId ?? '');
    setPurpose(
      appointment.meetingPurpose ??
        // Best-effort back-fill from legacy `type` if no purpose set.
        (appointment.type === 'site_visit'
          ? 'site_measurement'
          : appointment.type === 'office_meeting'
            ? 'initial_consultation'
            : appointment.type === 'virtual_call'
              ? 'initial_consultation'
              : 'other'),
    );
    setDate(extractISODate(sched));
    setStartTime(extractHHMM(sched));
    setEndTime(addMinutes(extractHHMM(sched), mins));
    setLocationMode(
      appointment.locationMode ??
        (appointment.type === 'virtual_call'
          ? 'online'
          : appointment.type === 'office_meeting'
            ? 'office'
            : appointment.type === 'site_visit'
              ? 'site'
              : 'other'),
    );
    setLocationText(appointment.location ?? '');
    setClientName(appointment.clientName ?? '');
    setClientPhone(appointment.clientPhone ?? '');
    setAssignedToName(
      appointment.assignedToName ?? appointment.attendees?.[0] ?? '',
    );
    setNotes(appointment.notes ?? '');
    setError(null);
  }, [open, mode, appointment]);

  // Esc + body scroll lock while open.
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !busy) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
    };
  }, [open, busy, onClose]);

  // Lead picker — auto-fill clientName/clientPhone when a lead is selected.
  const leadOptions = useMemo(() => {
    return leads.map((l) => ({
      value: l.id,
      label: `${l.name}${l.phone ? ` (${l.phone})` : ''}`,
    }));
  }, [leads]);

  const onLeadChange = (id: string) => {
    setLeadId(id);
    if (!id) {
      setClientName('');
      setClientPhone('');
      return;
    }
    const l = leads.find((x) => x.id === id);
    if (l) {
      setClientName(l.name ?? '');
      setClientPhone(l.phone ?? '');
    }
  };

  // Auto-fill / placeholder behaviour on locationMode change.
  const onLocationModeChange = (m: LocationMode) => {
    setLocationMode(m);
    // Don't OVERWRITE user input; only clear if empty so they can re-pick.
    if (m === 'office' && !locationText) {
      setLocationText('Praxis Studio Office');
    }
  };

  const locationPlaceholder = useMemo(() => {
    switch (locationMode) {
      case 'site':   return "Project address (we'll inherit from the lead if blank)";
      case 'office': return 'Our office address';
      case 'online': return 'Zoom / Google Meet / Teams URL';
      default:       return 'Address or link';
    }
  }, [locationMode]);

  const handleClose = () => {
    if (busy) return;
    onClose();
  };

  const handleSave = async () => {
    setError(null);
    if (!orgId) return setError('No organisation in scope.');
    if (!user) return setError('You must be signed in.');
    const t = title.trim();
    if (t.length < 2) return setError('Meeting title is required (2+ chars).');
    if (!date) return setError('Pick a date.');
    if (!leadId && !clientName.trim()) {
      return setError('Pick a lead/client or enter a client name.');
    }

    const scheduledAt = combineDateTime(date, startTime);
    const durationMins = minutesBetween(startTime, endTime || addMinutes(startTime, 60));
    const finalType = legacyTypeFor(purpose, locationMode);

    setBusy(true);
    try {
      if (mode?.kind === 'edit') {
        await updateAppointment(mode.apptId, {
          title: t,
          type: finalType,
          meetingPurpose: purpose,
          locationMode,
          scheduledAt,
          durationMins,
          location: locationText.trim() ? locationText.trim() : null,
          leadId: leadId || null,
          clientName: clientName.trim() ? clientName.trim() : null,
          clientPhone: clientPhone.trim() ? clientPhone.trim() : null,
          assignedToName: assignedToName.trim() ? assignedToName.trim() : null,
          notes: notes.trim() ? notes.trim() : null,
        });
        onClose();
        navigate(`/calendar/${mode.apptId}`);
      } else {
        const id = await createAppointment({
          orgId,
          createdBy: user.uid,
          title: t,
          type: finalType,
          meetingPurpose: purpose,
          locationMode,
          status: 'scheduled',
          scheduledAt,
          durationMins,
          location: locationText.trim() || undefined,
          leadId: leadId || undefined,
          clientName: clientName.trim() || undefined,
          clientPhone: clientPhone.trim() || undefined,
          assignedToName: assignedToName.trim() || undefined,
          notes: notes.trim() || undefined,
        });
        onClose();
        navigate(`/calendar/${id}`);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save appointment.');
      setBusy(false);
    }
  };

  return (
    <>
      <div
        className={`slide-overlay${open ? ' open' : ''}`}
        onClick={handleClose}
        aria-hidden
      />
      <div
        className={`slide-over${open ? ' open' : ''}`}
        role="dialog"
        aria-label={isEdit ? 'Edit appointment' : 'Schedule appointment'}
        aria-hidden={!open}
      >
        <div className="slide-header">
          <h2 className="slide-title">
            {isEdit ? 'Edit Appointment' : 'Schedule Appointment'}
          </h2>
          <button
            className="slide-close"
            type="button"
            onClick={handleClose}
            aria-label="Close"
          >
            <i className="ph ph-x" />
          </button>
        </div>

        <div className="slide-body">
          {error ? (
            <div
              className="card"
              style={{
                padding: 12,
                background: 'var(--critical-bg)',
                color: 'var(--critical-text)',
                borderColor: 'var(--critical)',
              }}
            >
              <i className="ph ph-warning-circle" /> {error}
            </div>
          ) : null}

          <form
            className="q-form"
            onSubmit={(e) => {
              e.preventDefault();
              handleSave();
            }}
            style={{ display: 'flex', flexDirection: 'column', gap: 20, padding: 4 }}
          >
            {/* 1 — General Details */}
            <div style={{ border: 'none', padding: 0 }}>
              <div className="form-group">
                <label style={STYLE_LABEL}>
                  Meeting Title <span style={STYLE_REQUIRED}>*</span>
                </label>
                <input
                  type="text"
                  placeholder="e.g. Site Measurement"
                  style={STYLE_INPUT}
                  value={title}
                  onChange={(e) => setTitle(e.currentTarget.value)}
                  disabled={busy}
                  required
                  maxLength={120}
                />
              </div>
              <div className="form-group" style={STYLE_GROUP}>
                <label style={STYLE_LABEL}>
                  Client / Lead <span style={STYLE_REQUIRED}>*</span>
                </label>
                <select
                  style={STYLE_INPUT}
                  value={leadId}
                  onChange={(e) => onLeadChange(e.currentTarget.value)}
                  disabled={busy}
                >
                  <option value="">Select an active lead or client…</option>
                  {leadOptions.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
                {!leadId ? (
                  <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                    <input
                      type="text"
                      placeholder="…or enter client name"
                      style={STYLE_INPUT}
                      value={clientName}
                      onChange={(e) => setClientName(e.currentTarget.value)}
                      disabled={busy}
                    />
                    <input
                      type="tel"
                      placeholder="Phone (optional)"
                      style={STYLE_INPUT}
                      value={clientPhone}
                      onChange={(e) => setClientPhone(e.currentTarget.value)}
                      disabled={busy}
                    />
                  </div>
                ) : null}
              </div>
              <div className="form-group" style={STYLE_GROUP}>
                <label style={STYLE_LABEL}>Meeting Type</label>
                <select
                  style={STYLE_INPUT}
                  value={purpose}
                  onChange={(e) => setPurpose(e.currentTarget.value as MeetingPurpose)}
                  disabled={busy}
                >
                  {MEETING_PURPOSE_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* 2 — Time & Location */}
            <div style={STYLE_SECTION}>
              <div className="form-group">
                <label style={STYLE_LABEL}>
                  Date <span style={STYLE_REQUIRED}>*</span>
                </label>
                <input
                  type="date"
                  style={STYLE_INPUT}
                  value={date}
                  onChange={(e) => setDate(e.currentTarget.value)}
                  disabled={busy}
                  required
                />
              </div>
              <div style={{ display: 'flex', gap: 12, marginTop: 12 }}>
                <div className="form-group" style={{ flex: 1 }}>
                  <label style={STYLE_LABEL}>
                    Start Time <span style={STYLE_REQUIRED}>*</span>
                  </label>
                  <input
                    type="time"
                    style={STYLE_INPUT}
                    value={startTime}
                    onChange={(e) => {
                      const next = e.currentTarget.value;
                      setStartTime(next);
                      // Auto-shift end time to maintain duration when
                      // the user moves the start.
                      const mins = minutesBetween(startTime, endTime);
                      setEndTime(addMinutes(next, mins));
                    }}
                    disabled={busy}
                    required
                  />
                </div>
                <div className="form-group" style={{ flex: 1 }}>
                  <label style={STYLE_LABEL}>End Time</label>
                  <input
                    type="time"
                    style={STYLE_INPUT}
                    value={endTime}
                    onChange={(e) => setEndTime(e.currentTarget.value)}
                    disabled={busy}
                  />
                </div>
              </div>
              <div className="form-group" style={STYLE_GROUP}>
                <label style={STYLE_LABEL}>Location Mode</label>
                <select
                  style={STYLE_INPUT}
                  value={locationMode}
                  onChange={(e) => onLocationModeChange(e.currentTarget.value as LocationMode)}
                  disabled={busy}
                >
                  {LOCATION_MODE_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </div>
              <div className="form-group" style={STYLE_GROUP}>
                <label style={STYLE_LABEL}>Address / Link</label>
                <input
                  type="text"
                  placeholder={locationPlaceholder}
                  style={STYLE_INPUT}
                  value={locationText}
                  onChange={(e) => setLocationText(e.currentTarget.value)}
                  disabled={busy}
                />
              </div>
              <div className="form-group" style={STYLE_GROUP}>
                <label style={STYLE_LABEL}>Assigned Staff</label>
                <input
                  type="text"
                  placeholder="e.g. Ananya Prasad"
                  style={STYLE_INPUT}
                  value={assignedToName}
                  onChange={(e) => setAssignedToName(e.currentTarget.value)}
                  disabled={busy}
                />
              </div>
            </div>

            {/* 3 — Notes */}
            <div style={STYLE_SECTION}>
              <div className="form-group">
                <label style={STYLE_LABEL}>Agenda &amp; Notes</label>
                <textarea
                  rows={4}
                  placeholder="List items to discuss…"
                  style={{ ...STYLE_INPUT, resize: 'vertical' }}
                  value={notes}
                  onChange={(e) => setNotes(e.currentTarget.value)}
                  disabled={busy}
                />
              </div>
            </div>

            <div
              style={{
                display: 'flex',
                gap: 12,
                marginTop: 10,
                paddingTop: 20,
                borderTop: '1px solid var(--border)',
              }}
            >
              <button
                type="button"
                className="btn-secondary"
                style={{ flex: 1, justifyContent: 'center' }}
                onClick={handleClose}
                disabled={busy}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn-primary"
                style={{ flex: 1, justifyContent: 'center' }}
                disabled={busy || !orgId}
              >
                {busy ? 'Saving…' : isEdit ? 'Save changes' : 'Schedule'}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Helper class polyfill — `.form-group` is referenced by the
          folder template but isn't in iio.css. Add a minimal block-level
          rule here via a runtime style tag (cheap; no global churn). */}
      {open ? (
        <style>{`.form-group { display: block; }`}</style>
      ) : null}
    </>
  );
}
